import React from "react";
import GenerationMixChart from "./GenerationMixChart";
import { useGenerationMix } from "../hooks/useGridData";
import {
  type GridMetricKey,
  METRIC_LABELS,
  METRIC_UNITS,
} from "../../../api/grid";

interface ZoneDetailPanelProps {
  baCode: string;
  selectedMetric: GridMetricKey;
  selectedHour?: string;
  onClose: () => void;
  isBaselineMode?: boolean;
  metricScale?: string;
  nationalAverage?: number | null;
  baselineRegionData?: any;
  pipelineData?: any;
}

/** Map snapshot metric keys to baseline per-MWh field names */
const METRIC_TO_BASELINE_FIELD: Record<string, string> = {
  carbon_intensity_kg_per_mwh: "carbon_intensity_kg_per_mwh",
  human_health_dalys: "human_health_dalys_per_mwh",
  ecosystem_quality_species_yr: "ecosystem_quality_species_yr_per_mwh",
  resource_scarcity_usd2013: "resource_scarcity_usd_per_mwh",
};

/** Format a metric value for display */
function fmtVal(val: number | null | undefined): string {
  if (val == null) return "—";
  const abs = Math.abs(val);
  if (abs >= 1_000_000) return val.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 100) return val.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 1) return val.toFixed(2);
  if (abs >= 0.01) return val.toFixed(3);
  if (abs > 0) return val.toExponential(2);
  return "0";
}

/** Short display code */
function shortCode(code: string): string {
  if (code.startsWith("US-")) return code.split("-").pop() || code;
  return code;
}

const ZoneDetailPanel: React.FC<ZoneDetailPanelProps> = ({
  baCode,
  selectedMetric,
  selectedHour,
  onClose,
  baselineRegionData,
}) => {
  const { data: genMix } = useGenerationMix(baCode, undefined, "annual");
  const d = baselineRegionData;
  const code = shortCode(baCode);

  // Get the selected metric's value
  // The baseline data maps per-MWh rates to standard field names (human_health_dalys, etc.)
  const metricVal = d?.[selectedMetric] as number | null | undefined;
  const metricLabel = METRIC_LABELS[selectedMetric] || selectedMetric;
  const metricUnit = METRIC_UNITS[selectedMetric] || "";

  return (
    <div className="fixed right-0 top-[52px] bottom-0 w-80 bg-surface-800 border-l border-border z-40 overflow-y-auto shadow-2xl animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div>
          <h3 className="text-h4 text-text-primary font-semibold">{code}</h3>
          <p className="text-caption text-text-muted">Annual baseline (2025)</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-surface-700 text-text-muted hover:text-text-primary transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="p-4 space-y-5">
        {/* Selected metric headline */}
        {d && (
          <div>
            <p className="text-caption text-text-muted uppercase tracking-wider mb-1">
              {metricLabel}
            </p>
            <p className="text-2xl font-bold text-text-primary">
              {fmtVal(metricVal)}
              <span className="text-body-sm text-text-muted ml-2">{metricUnit}</span>
            </p>
          </div>
        )}

        {/* Key stats grid */}
        {d && (
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
            <div>
              <p className="text-caption text-text-muted">Annual Generation</p>
              <p className="text-body font-medium text-text-primary">
                {d.total_generation_mwh >= 1e6
                  ? `${(d.total_generation_mwh / 1e6).toFixed(1)} TWh`
                  : d.total_generation_mwh >= 1e3
                    ? `${(d.total_generation_mwh / 1e3).toFixed(0)} GWh`
                    : `${d.total_generation_mwh?.toFixed(0) ?? "—"} MWh`}
              </p>
            </div>
            <div>
              <p className="text-caption text-text-muted">Carbon Intensity</p>
              <p className="text-body font-medium text-text-primary">
                {fmtVal(d.carbon_intensity_kg_per_mwh)} <span className="text-caption text-text-muted">kg CO₂/MWh</span>
              </p>
            </div>
            <div>
              <p className="text-caption text-text-muted">Health Impact</p>
              <p className="text-body font-medium text-text-primary">
                {fmtVal(d.human_health_dalys)} <span className="text-caption text-text-muted">DALYs/MWh</span>
              </p>
            </div>
            <div>
              <p className="text-caption text-text-muted">Ecosystem</p>
              <p className="text-body font-medium text-text-primary">
                {fmtVal(d.ecosystem_quality_species_yr)} <span className="text-caption text-text-muted">species.yr/MWh</span>
              </p>
            </div>
            <div>
              <p className="text-caption text-text-muted">Resource Cost</p>
              <p className="text-body font-medium text-text-primary">
                ${fmtVal(d.resource_scarcity_usd2013)} <span className="text-caption text-text-muted">/MWh</span>
              </p>
            </div>
          </div>
        )}

        {/* Generation Mix */}
        {genMix && genMix.fuels.length > 0 && (
          <div className="pt-2 border-t border-border">
            <p className="text-caption text-text-muted mb-2">Generation Mix</p>
            <GenerationMixChart fuels={genMix.fuels} compact />
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoneDetailPanel;
